/**
 * Created by neo on 10.01.21.
 */
import { makeAutoObservable } from 'mobx';
import { ExploreEntry, createExploreEntry } from './ExploreEntry';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { v1ParamsToV3Params } from './exploreV3Params';
import { ExploreEntryQueryRequest } from './ExploreEntryQueryRequest';
import { Activity } from '../Activity/Activity';
import { ActivityLog, newActivityLog } from '../Activity/ActivityLog';
import { Instructor, createInstructor } from './Instructor';

export type ActivityWorkoutEntryBase = ExploreEntry & {
  activityIdentifier: string;
  data: Record<string, any>;
  instructor?: Instructor;
  recommendedMusicKey?: string;
  durationInMinutes?: number;
  activity?: Activity;
  createActivityLog(startDate: Date, endDate: Date, data?: Record<string, number>): ActivityLog;
};

export type ActivityWorkoutEntry = ActivityWorkoutEntryBase & {
  type: 'activityWorkout';
};

export type ActivityWorkoutEntryType = 'activityWorkout' | 'meditation';

export function createActivityWorkoutEntryBase<T extends ActivityWorkoutEntryType>(
  json: Partial<ActivityWorkoutEntryBase> | undefined,
  type: T,
  activityIdentifier?: string,
) {
  const base = createExploreEntry({ ...json, type });
  return {
    ...base,
    type,
    activityIdentifier: activityIdentifier ?? json?.activityIdentifier ?? '',
    data: json?.data ?? {},
    instructor: json?.instructor ? createInstructor(json.instructor as any) : undefined,
    recommendedMusicKey: json?.recommendedMusicKey,
    durationInMinutes: json?.durationInMinutes,
    activity: undefined as Activity | undefined,

    createActivityLog(startDate: Date, endDate: Date, data: Record<string, number> = {}): ActivityLog {
      if (!data.DURATION) {
        data.DURATION = Math.round((endDate.valueOf() - startDate.valueOf()) / 1000);
      }
      return newActivityLog({
        type: this.activityIdentifier,
        source: {
          source: 'kinastic',
          sourceId: this.id,
          sourceType: 'explore-entry',
        },
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        data,
      });
    },
  };
}

export function createActivityWorkoutEntry(json?: Partial<ActivityWorkoutEntry>): ActivityWorkoutEntry {
  return makeAutoObservable(createActivityWorkoutEntryBase(json, 'activityWorkout'));
}

import type { MeditationEntry } from './MeditationEntry';

export type ActivityWorkoutEntryUnion = ActivityWorkoutEntry | MeditationEntry;

export const ActivityWorkoutEntryApi = {
  find(request: Omit<ExploreEntryQueryRequest, 'type'>): Promise<ActivityWorkoutEntry[]> {
    return HttpBackend.get(`/coach/explore/v3/entries`, v1ParamsToV3Params({ ...request, type: 'activityWorkout' })).then((res) =>
      (res ?? []).map((r: any) => createActivityWorkoutEntry(r)),
    );
  },

  findOne(id: string): Promise<ActivityWorkoutEntry> {
    return HttpBackend.get(`/coach/explore/v3/entries/${id}`).then((res) => createActivityWorkoutEntry(res));
  },
};
